import {
  IColumnPreferencesRepository,
  IColumnPreferencePresetApi,
} from "../../adapters/driven/repositories/columnPreferencesRepository";

export interface IColumnPreferencePreset {
  presetId: string;
  name: string;
  selectedColumns: string[];
  createdAt: string;
  updatedAt: string;
}

export interface IColumnPreferencesResult {
  viewKey: string;
  selectedColumns: string[];
  updatedAt: string;
  presets: IColumnPreferencePreset[];
  /**
   * Server-authoritative id of the preset whose columns currently match the
   * global selection. `null` means the selection was manually edited (no
   * preset pinned).
   */
  appliedPresetId: string | null;
}

/**
 * Result of creating or updating a preset. The backend returns the mutated
 * preset plus the refreshed preset library so the UI can re-render the full
 * list without a follow-up GET.
 */
export interface IColumnPreferencePresetMutationResult {
  preset: IColumnPreferencePreset;
  presets: IColumnPreferencePreset[];
}

export interface IColumnPreferencesServiceError {
  /** Machine-readable code so callers can branch (e.g. show "Duplicate name"). */
  code:
    | "NOT_FOUND"
    | "DUPLICATE_NAME"
    | "LIMIT_REACHED"
    | "VALIDATION"
    | "GENERIC";
  message: string;
}

// APIProvider.GET swallows HTTP errors and returns the response body as data,
// so we detect error shapes by inspecting the returned payload rather than
// catching a thrown exception.
interface IFastApiDetailShape {
  detail?:
    | string
    | { message?: string; code?: string }
    | { message?: string; code?: string }[];
}

function extractDetailMessage(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const d = data as IFastApiDetailShape;
  if (!d.detail) return null;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail)) {
    const first = d.detail[0];
    if (first && typeof first === "object" && "message" in first) {
      return (first.message as string) ?? null;
    }
    return null;
  }
  if (typeof d.detail === "object" && "message" in d.detail) {
    return (d.detail.message as string) ?? null;
  }
  return null;
}

function looksLikeDetailPayload(data: unknown): boolean {
  return !!(
    data &&
    typeof data === "object" &&
    (data as IFastApiDetailShape).detail !== undefined
  );
}

function classifyErrorMessage(message: string | null): {
  code: IColumnPreferencesServiceError["code"];
  message: string;
} {
  const safe = (message || "").toLowerCase();
  if (safe.includes("not found") || safe.includes("no column preference")) {
    return { code: "NOT_FOUND", message: message || "Not found" };
  }
  if (safe.includes("already exists") || safe.includes("duplicate")) {
    return {
      code: "DUPLICATE_NAME",
      message: message || "A preset with that name already exists",
    };
  }
  if (safe.includes("maximum") || safe.includes("limit")) {
    return {
      code: "LIMIT_REACHED",
      message: message || "Preset limit reached",
    };
  }
  if (
    safe.includes("invalid") ||
    safe.includes("must") ||
    safe.includes("required")
  ) {
    return { code: "VALIDATION", message: message || "Invalid request" };
  }
  return { code: "GENERIC", message: message || "Request failed" };
}

function mapPreset(p: IColumnPreferencePresetApi): IColumnPreferencePreset {
  return {
    presetId: p.preset_id,
    name: p.name,
    selectedColumns: Array.isArray(p.selected_columns)
      ? p.selected_columns
      : [],
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  };
}

/** Normalize the server's optional/nullable applied_preset_id into a string | null. */
function readAppliedPresetId(
  data: { applied_preset_id?: string | null } | null | undefined
): string | null {
  if (!data) return null;
  return typeof data.applied_preset_id === "string"
    ? data.applied_preset_id
    : null;
}

export const ColumnPreferencesServiceImplFactory = ({
  columnPreferencesRepository,
}: {
  columnPreferencesRepository: IColumnPreferencesRepository;
}) => {
  const getColumnPreferences = async (
    viewKey: string
  ): Promise<
    IControllerPromiseResponse<
      IColumnPreferencesResult,
      IColumnPreferencesServiceError
    >
  > => {
    try {
      const data = await columnPreferencesRepository.getColumnPreferences(
        viewKey
      );

      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }

      return {
        success: true,
        data: {
          viewKey: data.view_key,
          selectedColumns: data.selected_columns ?? [],
          updatedAt: data.updated_at,
          presets: (data.presets ?? []).map(mapPreset),
          appliedPresetId: readAppliedPresetId(data),
        },
      };
    } catch (error: unknown) {
      console.error(
        "[ColumnPreferencesService] getColumnPreferences error:",
        error
      );
      return {
        success: false,
        error: {
          code: "GENERIC",
          message: "Failed to fetch column preferences",
        },
      };
    }
  };

  /**
   * Save the global selection from an explicit column list (manual toggle).
   * Presets are untouched.
   */
  const saveColumnPreferences = async (
    viewKey: string,
    selectedColumns: string[]
  ): Promise<
    IControllerPromiseResponse<
      IColumnPreferencesResult,
      IColumnPreferencesServiceError
    >
  > => {
    try {
      const data = await columnPreferencesRepository.putColumnPreferences(
        viewKey,
        { selected_columns: selectedColumns }
      );
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }
      return {
        success: true,
        data: {
          viewKey: data.view_key,
          selectedColumns: data.selected_columns ?? [],
          updatedAt: data.updated_at,
          presets: (data.presets ?? []).map(mapPreset),
          appliedPresetId: readAppliedPresetId(data),
        },
      };
    } catch (error: unknown) {
      console.error(
        "[ColumnPreferencesService] saveColumnPreferences error:",
        error
      );
      return {
        success: false,
        error: {
          code: "GENERIC",
          message: "Failed to save column preferences",
        },
      };
    }
  };

  const createPreset = async (
    viewKey: string,
    name: string,
    selectedColumns: string[]
  ): Promise<
    IControllerPromiseResponse<
      IColumnPreferencePresetMutationResult,
      IColumnPreferencesServiceError
    >
  > => {
    try {
      const data =
        await columnPreferencesRepository.createColumnPreferencePreset({
          view_key: viewKey,
          name,
          selected_columns: selectedColumns,
        });
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }
      return {
        success: true,
        data: {
          preset: mapPreset(data.preset),
          presets: (data.presets ?? []).map(mapPreset),
        },
      };
    } catch (error: unknown) {
      console.error("[ColumnPreferencesService] createPreset error:", error);
      return {
        success: false,
        error: { code: "GENERIC", message: "Failed to create preset" },
      };
    }
  };

  const updatePreset = async (
    viewKey: string,
    presetId: string,
    updates: { name?: string; selectedColumns?: string[] }
  ): Promise<
    IControllerPromiseResponse<
      IColumnPreferencePresetMutationResult,
      IColumnPreferencesServiceError
    >
  > => {
    try {
      const payload: {
        name?: string;
        selected_columns?: string[];
      } = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.selectedColumns !== undefined) {
        payload.selected_columns = updates.selectedColumns;
      }
      const data =
        await columnPreferencesRepository.updateColumnPreferencePreset(
          viewKey,
          presetId,
          payload
        );
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }
      return {
        success: true,
        data: {
          preset: mapPreset(data.preset),
          presets: (data.presets ?? []).map(mapPreset),
        },
      };
    } catch (error: unknown) {
      console.error("[ColumnPreferencesService] updatePreset error:", error);
      return {
        success: false,
        error: { code: "GENERIC", message: "Failed to update preset" },
      };
    }
  };

  const deletePreset = async (
    viewKey: string,
    presetId: string
  ): Promise<
    IControllerPromiseResponse<true, IColumnPreferencesServiceError>
  > => {
    try {
      const data =
        await columnPreferencesRepository.deleteColumnPreferencePreset(
          viewKey,
          presetId
        );
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }
      return { success: true, data: true };
    } catch (error: unknown) {
      console.error("[ColumnPreferencesService] deletePreset error:", error);
      return {
        success: false,
        error: { code: "GENERIC", message: "Failed to delete preset" },
      };
    }
  };

  /**
   * Apply a preset: server copies the preset's columns into the global
   * selection and returns the refreshed preference payload.
   */
  const applyPreset = async (
    viewKey: string,
    presetId: string
  ): Promise<
    IControllerPromiseResponse<
      IColumnPreferencesResult,
      IColumnPreferencesServiceError
    >
  > => {
    try {
      const data =
        await columnPreferencesRepository.applyColumnPreferencePreset(
          viewKey,
          presetId
        );
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: classifyErrorMessage(extractDetailMessage(data)),
        };
      }
      return {
        success: true,
        data: {
          viewKey: data.view_key,
          selectedColumns: data.selected_columns ?? [],
          updatedAt: data.updated_at,
          presets: (data.presets ?? []).map(mapPreset),
          appliedPresetId: readAppliedPresetId(data),
        },
      };
    } catch (error: unknown) {
      console.error("[ColumnPreferencesService] applyPreset error:", error);
      return {
        success: false,
        error: { code: "GENERIC", message: "Failed to apply preset" },
      };
    }
  };

  return {
    getColumnPreferences,
    saveColumnPreferences,
    createPreset,
    updatePreset,
    deletePreset,
    applyPreset,
  };
};
